import './assets/css/App.css';
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import AuthLayout from './layouts/auth';
import AdminLayout from './layouts/admin';
import { ChakraProvider } from '@chakra-ui/react';
import initialTheme from './theme/theme';
import { useEffect, useState, useRef } from 'react';
import OfflinePage from 'components/OfflinePage';
import { RegionDataProvider } from './contexts/RegionDataContext';
import socketService from './services/socket';

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  return children;
};

const SocketConnectionManager = () => {
  const location = useLocation();
  const isInitialized = useRef(false);
  const isReconnecting = useRef(false);
  const checkInterval = useRef(null);
  const socketCleanupRef = useRef(null);

  const initializeSocket = () => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (!token || !userStr) {
      socketService.disconnect();
      isInitialized.current = false;
      return;
    }

    let user;
    try {
      user = JSON.parse(userStr);
    } catch (error) {
      return;
    }

    const status = socketService.getStatus();

    if (!status.isConnected && !isReconnecting.current) {
      isReconnecting.current = true;

      try {
        socketService.connect();
        isInitialized.current = true;

        socketCleanupRef.current = {
          userId: user._id || user.id,
          initializedAt: Date.now(),
        };
      } catch (error) {
        isInitialized.current = false;
      } finally {
        setTimeout(() => {
          isReconnecting.current = false;
        }, 3000);
      }
    } else if (status.isConnected) {
      isInitialized.current = true;
    }
  };

  const cleanupSocket = () => {
    socketService.disconnect();
    isInitialized.current = false;
    isReconnecting.current = false;
    socketCleanupRef.current = null;
  };

  const startConnectionMonitor = () => {
    if (checkInterval.current) {
      clearInterval(checkInterval.current);
    }

    checkInterval.current = setInterval(() => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          const status = socketService.getStatus();

          if (!status.isConnected && !isReconnecting.current) {
            isReconnecting.current = true;

            setTimeout(() => {
              initializeSocket();
              setTimeout(() => {
                isReconnecting.current = false;
              }, 2000);
            }, 1000);
          }
        } catch (error) {}
      } else {
        if (isInitialized.current) {
          cleanupSocket();
        }
      }
    }, 30000);
  };

  const handleLogout = () => {
    cleanupSocket();
  };

  const handleStorageChange = (e) => {
    if (e.key === 'token' && !e.newValue) {
      cleanupSocket();
    }

    if (e.key === 'token' && e.newValue && !isInitialized.current) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        setTimeout(() => {
          initializeSocket();
        }, 1000);
      }
    }
  };

  const handleOnline = () => {
    const status = socketService.getStatus();
    if (!status.isConnected) {
      setTimeout(() => {
        initializeSocket();
      }, 2000);
    }
  };

  const handleOffline = () => {};

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      const status = socketService.getStatus();

      if (!status.isConnected && !isReconnecting.current) {
        setTimeout(() => {
          initializeSocket();
        }, 1000);
      }
    }
  };

  useEffect(() => {
    initializeSocket();

    startConnectionMonitor();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('visibilitychange', handleVisibilityChange);

    window.addEventListener('user_logout', handleLogout);

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('user_logout', handleLogout);
      window.removeEventListener('storage', handleStorageChange);

      if (checkInterval.current) {
        clearInterval(checkInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr && !isInitialized.current) {
      const status = socketService.getStatus();
      if (!status.isConnected) {
        setTimeout(() => {
          initializeSocket();
        }, 500);
      }
    }
  }, [location]);

  return null;
};

export default function Main() {
  const [currentTheme, setCurrentTheme] = useState(initialTheme);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (!isOnline) return <OfflinePage />;

  return (
    <ChakraProvider theme={currentTheme}>
      {localStorage.getItem('token') && <SocketConnectionManager />}

      <Routes>
        <Route path="auth/*" element={<AuthLayout />} />

        <Route
          path="admin/*"
          element={
            <ProtectedRoute>
              <RegionDataProvider>
                <AdminLayout theme={currentTheme} setTheme={setCurrentTheme} />
              </RegionDataProvider>
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={
            localStorage.getItem('token') ? (
              <Navigate to="/admin" replace />
            ) : (
              <Navigate to="/auth/sign-in" replace />
            )
          }
        />
      </Routes>
    </ChakraProvider>
  );
}
